import React, { useRef } from 'react';
import type { View, ViewProps } from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import {
  BodyText,
  NavbarLinkBadge,
  usePressableState,
} from '@sweetgreen/garnish';

import { concatTestID } from '../../utils';
import { DESKTOP_HEADER_ITEM_SPACING } from './constants';
import type { DesktopHeaderItemProps } from './DesktopHeader.types';

// ────────────────────────────────────────────────────────────────────────────────

export const DesktopHeaderTextItem = (props: DesktopHeaderTextItemProps) => {
  const {
    title,
    isActive,
    hasBadge,
    style,
    accessibilityLabel,
    testID,
    ...restProps
  } = props;

  const pressableRef = useRef<View>(null);
  const pressableState = usePressableState(pressableRef);
  const pressableStyles = [styles.pressable, style];

  const textStyles = [
    styles.text,
    pressableState.isHovered || isActive ? styles.textActive : {},
  ];

  // ─────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={pressableRef}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? title}
      style={pressableStyles}
      {...restProps}
    >
      <BodyText size={3} style={textStyles} numberOfLines={1}>
        {title}
      </BodyText>

      {hasBadge ? (
        <NavbarLinkBadge testID={concatTestID(testID, '.badge')} />
      ) : null}
    </Pressable>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    marginHorizontal: DESKTOP_HEADER_ITEM_SPACING,
  },
  text: {
    textTransform: 'uppercase',
  },
  textActive: {
    textDecorationLine: 'underline',
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type DesktopHeaderTextItemProps = {
  readonly title: string;
  readonly isActive: boolean;
  readonly hasBadge?: boolean;
  readonly style?: ViewProps['style'];
} & DesktopHeaderItemProps;
